import { useAuth } from "../../lib/auth";
import { ShieldCheck, Mail, KeyRound, ExternalLink } from "lucide-react";

const Row = ({ icon: Icon, label, value, href }) => {
  const inner = (
    <div className="flex items-start gap-4 px-5 py-4 border-b border-[var(--ed-rule)] last:border-b-0">
      <div className="w-10 h-10 rounded-full bg-[var(--ed-emerald-tint)] text-[var(--ed-emerald)] flex items-center justify-center flex-shrink-0">
        <Icon size={15} strokeWidth={1.6} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[0.6rem] uppercase tracking-[0.22em] text-[var(--ed-ink-muted)] mb-1">{label}</p>
        <p className="text-[var(--ed-ink)] text-sm break-all">{value}</p>
      </div>
      {href && (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-[var(--ed-ink-muted)] hover:text-[var(--ed-emerald)] flex-shrink-0 mt-1">
          <ExternalLink size={14} />
        </a>
      )}
    </div>
  );
  return inner;
};

export default function SettingsPanel() {
  const { user } = useAuth();
  return (
    <div data-testid="settings-panel">
      <header className="mb-8">
        <p className="ed-overline mb-2">System</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-[var(--ed-ink)]">Settings</h1>
        <p className="text-[var(--ed-ink-muted)] text-sm mt-1.5">Account, integrations, and studio info.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="border border-[var(--ed-rule)] bg-[var(--ed-bg)] rounded-sm">
          <div className="px-5 py-4 border-b border-[var(--ed-rule)] bg-[var(--ed-paper)]/40">
            <h2 className="font-serif text-lg text-[var(--ed-ink)]">Your account</h2>
          </div>
          <Row icon={Mail}         label="Email"  value={user?.email || "—"} />
          <Row icon={ShieldCheck}  label="Role"   value={user?.is_admin ? "Administrator" : "Editor"} />
          <Row icon={KeyRound}     label="Auth"   value="Emergent-managed Google Sign-in" />
        </section>

        <section className="border border-[var(--ed-rule)] bg-[var(--ed-bg)] rounded-sm">
          <div className="px-5 py-4 border-b border-[var(--ed-rule)] bg-[var(--ed-paper)]/40">
            <h2 className="font-serif text-lg text-[var(--ed-ink)]">Integrations</h2>
          </div>
          <Row icon={KeyRound} label="Payments"   value="Stripe Checkout (test + live keys via .env)"        href="https://dashboard.stripe.com" />
          <Row icon={Mail}     label="Email"      value="Resend (transactional · with ICS calendar invites)" href="https://resend.com" />
          <Row icon={ShieldCheck} label="Auth"    value="Emergent Google Sign-in (admin allow-list)" />
        </section>
      </div>

      <section className="mt-6 border border-[var(--ed-rule)] bg-[var(--ed-paper)]/40 rounded-sm px-6 py-5">
        <p className="ed-overline text-[var(--ed-emerald)] mb-2">Tip</p>
        <h3 className="font-serif text-xl text-[var(--ed-ink)] mb-2">Everything on the public site is editable.</h3>
        <p className="text-[var(--ed-ink-soft)] text-sm leading-relaxed max-w-2xl">
          Use the <strong>Homepage</strong>, <strong>SEO</strong>, and <strong>Media Library</strong> tabs to update copy, photographs and metadata.
          Property records (with photos, pricing, amenities and policies) live in <strong>Properties</strong>. No developer required.
        </p>
      </section>
    </div>
  );
}
